/**
 * The merge tags a writer can reach for — 18 Sep 2026.
 *
 * The editor's half. `functions-webhook/src/mergeFields.ts` is the authority:
 * it decides what each tag actually becomes for each of forty-four thousand
 * people, and this file exists so the writer can see the list and put one in a
 * sentence without retyping braces.
 *
 * **The two lists are checked against each other**, by `scripts/checkMergeFields.ts`,
 * the same way `checkEmailVariants.ts` guards the section markers. Two packages
 * mean two copies, and a tag offered here that the sender does not know would
 * ship `{{routes}}` to somebody's inbox verbatim — which is exactly the failure
 * this table is supposed to prevent.
 *
 * ## Nothing in here touches the DOM
 *
 * Deliberately. `scripts/checkMergeFields.ts` imports this file to check it
 * against the sender's copy, and the scripts are compiled as Node with no DOM
 * library — so the one thing that does need a document, `useMergeInsert`, lives
 * in its own file beside this one.
 */
export interface MergeTag {
  tag: string
  label: string
  sample: string
  filled: 'always' | 'most' | 'some' | 'few'
  note?: string
}

/** Kept in step with the sender's copy — see the header. */
export const MERGE_TAGS: MergeTag[] = [
  { tag: 'firstname', label: 'First name', sample: 'Ingrid', filled: 'some', note: 'About half of app accounts have a name. Give it a fallback.' },
  { tag: 'name', label: 'Full name', sample: 'Ingrid Haugen', filled: 'some', note: 'The same half. Give it a fallback.' },
  { tag: 'email', label: 'Email address', sample: 'ingrid@example.com', filled: 'always' },
  { tag: 'routes', label: 'Routes drawn', sample: '17', filled: 'few', note: 'Only about 1,100 accounts have ever drawn one. Everyone else gets the fallback.' },
  { tag: 'collections', label: 'Collections made', sample: '3', filled: 'few', note: 'Rarer still — 475 in all, and the automatic Bookmarks list is not counted.' },
  { tag: 'bookmarks', label: 'Routes bookmarked', sample: '5', filled: 'few', note: 'Routes saved to Bookmarks. The commonest thing people do — 4,170 across about a thousand accounts — and still a small minority of the list.' },
  { tag: 'plan', label: 'Subscription plan', sample: 'Premium yearly', filled: 'few', note: 'Subscribers only, about one account in twenty.' },
  { tag: 'expires', label: 'Subscription ends', sample: '10.03.2027', filled: 'few', note: 'Subscribers only. Numeric, so it reads the same in any language.' },
  { tag: 'renews', label: 'Whether it renews', sample: 'renews automatically', filled: 'few', note: 'Subscribers only, and an English phrase. For a Norwegian send use “Subscription ends” with your own wording.' },
  { tag: 'tracker', label: 'Tracker connected', sample: 'Garmin', filled: 'few', note: '181 live connections. Empty for everyone else, including the ones that have stopped working.' },
]

/** How often each one is filled, in the words the panel shows. */
export const FILLED_LABEL: Record<MergeTag['filled'], string> = {
  always: 'everyone',
  most: 'nearly everyone',
  some: 'about half',
  few: 'a small minority',
}

const KNOWN = new Set(MERGE_TAGS.map((t) => t.tag))
const SAMPLE: Record<string, string> = Object.fromEntries(MERGE_TAGS.map((t) => [t.tag, t.sample]))

/** Mirrors the sender's parser exactly: `{{tag}}` or `{{tag|fallback}}`. */
const TAG = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*(?:\|([^}]*))?\}\}/g

/**
 * What the preview shows in place of the tags.
 *
 * Sample values rather than blanks: a preview with the tags left in reads as a
 * template, and a preview with them blanked reads as a bug. Neither shows the
 * writer whether the sentence they wrote works.
 *
 * Unknown tags are left visible on purpose — `{{navn}}`, typed out of habit from
 * the shareholder form, should look wrong on the screen rather than in an inbox.
 */
export function previewMerge(html: string, values?: Record<string, string>): string {
  if (!html.includes('{{')) return html
  // With a named reader, their own values and the writer's fallback where they
  // have none — exactly what `fillMerge` does on the way out, so the preview
  // shows the gaps a real recipient would see rather than papering over them
  // with an example. Without one, the samples.
  return html.replace(TAG, (whole, tag: string, fallback?: string) => {
    if (!KNOWN.has(tag)) return whole
    if (!values) return SAMPLE[tag] ?? fallback ?? ''
    const v = values[tag]
    return v !== undefined && v !== '' ? v : (fallback ?? '')
  })
}

/**
 * Upper-case the words but not the tags — 21 Sep 2026.
 *
 * The dated notice has a label that is meant to be in caps, and its field did
 * the obvious thing: `value.toUpperCase()` on every keystroke. Insert `{{plan}}`
 * into it and it became `{{PLAN}}` — a tag nothing fills, which the editor
 * reported as unknown and which would have gone out as five literal characters
 * in somebody's inbox.
 *
 * Matching tags case-insensitively would also have silenced the warning, and it
 * is the wrong fix: two spellings for one tag is a worse problem than one field
 * that shouts. A merge tag is an identifier, and identifiers are not styling.
 */
export function upperOutsideTags(value: string): string {
  let out = ''
  let i = 0
  for (const m of value.matchAll(TAG)) {
    out += value.slice(i, m.index).toUpperCase() + canonical(m[0], m[1]!)
    i = m.index + m[0].length
  }
  return out + value.slice(i).toUpperCase()
}

/**
 * A tag this field shouted at before the fix above, put back as it should be.
 *
 * `{{PLAN}}` is not a tag anybody typed — it is `{{plan}}` after a `toUpperCase`
 * that should never have touched it, and the drafts written before 21 Sep 2026
 * still hold it. Correcting it here means one keystroke in that field repairs a
 * draft rather than somebody hunting for what the "Unknown" warning is pointing
 * at.
 *
 * Narrow on purpose: only an exact case-insensitive match for a tag we know, and
 * only back to the one canonical spelling. It is a repair, not a second way of
 * writing a tag — `{{Plan}}` becoming valid is how you end up maintaining two.
 */
function canonical(whole: string, tag: string): string {
  if (KNOWN.has(tag)) return whole
  const match = MERGE_TAGS.find((t) => t.tag.toLowerCase() === tag.toLowerCase())
  return match ? whole.replace(tag, match.tag) : whole
}

/** Tags in this document that nothing will ever fill, so the editor can say so. */
export function unknownTags(html: string): string[] {
  const out = new Set<string>()
  for (const m of html.matchAll(TAG)) {
    const tag = m[1]!
    // The footer's two links are filled per recipient by `withFooterLinks`,
    // which is a different mechanism with the same syntax. They are not typos.
    if (!KNOWN.has(tag) && tag !== 'unsubscribe' && tag !== 'preferences') out.add(tag)
  }
  return [...out]
}
